import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { ArrowLeft, Upload, X } from "lucide-react";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
import { be_regions, fr_regions, lu_regions } from "bundles/common/utils/constants";
import sh from "bundles/common/utils/sh";
import { formatRegionMessage } from "bundles/common/utils/utils";
import DuplicateProfileDialog from "../pipeline/DuplicateProfileDialog";

const SOURCES = [
  { value: "forem", label: "Forem" },
  { value: "vdab", label: "VDAB" },
  { value: "stepstone", label: "Stepstone" },
  { value: "actiris", label: "Actiris" },
  { value: "france_travail", label: "France Travail" },
  { value: "apec", label: "APEC" },
  { value: "indeed", label: "Indeed" },
  { value: "linkedin", label: "LinkedIn" },
];

const emptyForm = {
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  region: "",
  desired_title: "",
  source: "",
};

/* Shared drawer for creating or editing an external profile. */
const CreateProfileDrawer = ({ open, onClose, pipelineId, onCreated, editingProfile, onUpdated }) => {
  const englishRegionGroups = useMemo(
    () => [
      { label: formatRegionMessage("belgium"), options: be_regions },
      { label: formatRegionMessage("france"), options: fr_regions },
      { label: formatRegionMessage("luxembourg"), options: lu_regions },
    ],
    [],
  );
  const authenticity_token = useSelector((state) => state.authenticity_token);

  const isEditMode = Boolean(editingProfile);
  const needsPipelinePicker = !isEditMode && !pipelineId;

  // Step: 1 = pick pipeline, 2 = fill form
  const [step, setStep] = useState(needsPipelinePicker ? 1 : 2);
  const [pipelines, setPipelines] = useState([]);
  const [pipelineSearch, setPipelineSearch] = useState("");
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [fetchingPipelines, setFetchingPipelines] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [cvFile, setCvFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const cvInputRef = useRef(null);

  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);

  // Duplicate handling
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateProfile, setDuplicateProfile] = useState(null);

  // Reset when drawer opens
  useEffect(() => {
    if (!open) return;

    if (isEditMode) {
      setForm({
        first_name: editingProfile.first_name || "",
        last_name: editingProfile.last_name || "",
        phone: editingProfile.phone || "",
        email: editingProfile.email || "",
        region: editingProfile.region || "",
        desired_title: editingProfile.desired_title || "",
        source: editingProfile.source || "",
      });
      setStep(2);
    } else {
      setForm(emptyForm);
    }

    setCvFile(null);
    setError("");
    setSaving(false);
    setDragging(false);
    dragCounter.current = 0;
    setDuplicateDialogOpen(false);
    setDuplicateProfile(null);

    if (needsPipelinePicker) {
      setStep(1);
      setSelectedPipeline(null);
      setPipelineSearch("");
      setFetchingPipelines(true);
      sh.get("/internal/search/profile_pipelines")
        .then((res) => setPipelines(Array.isArray(res.data) ? res.data : []))
        .catch(() => setPipelines([]))
        .finally(() => setFetchingPipelines(false));
    } else {
      setStep(2);
    }
  }, [open, needsPipelinePicker, isEditMode, editingProfile]);

  const filteredPipelines = useMemo(() => {
    if (!pipelineSearch.trim()) return pipelines;
    const q = pipelineSearch.toLowerCase();
    return pipelines.filter((p) => (p.title || "").toLowerCase().includes(q));
  }, [pipelines, pipelineSearch]);

  const resolvedPipelineId = pipelineId || selectedPipeline?.id;

  const handleSubmit = useCallback(() => {
    if (saving) return;

    const missingFields = [];
    if (!form.first_name.trim()) missingFields.push("first name");
    if (!form.last_name.trim()) missingFields.push("last name");
    if (missingFields.length > 0) {
      setError(`Please fill in: ${missingFields.join(", ")}.`);
      return;
    }
    if (isEditMode) {
      setSaving(true);
      setError("");

      const formData = new FormData();
      formData.append("authenticity_token", authenticity_token || "");
      Object.entries(form).forEach(([key, value]) => {
        formData.append(`external_profile[${key}]`, value || "");
      });
      if (cvFile) {
        formData.append("external_profile[cv]", cvFile);
      }

      sh.patch(`/internal/external_profiles/${editingProfile.id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
        .then((res) => {
          onUpdated?.(res.data);
          onClose();
        })
        .catch((err) => {
          const message = err?.response?.data?.errors?.join(", ") || "Could not update profile.";
          setError(message);
        })
        .finally(() => setSaving(false));
      return;
    }
    if (!resolvedPipelineId) return;

    setSaving(true);
    setError("");

    const formData = new FormData();
    formData.append("authenticity_token", authenticity_token || "");
    Object.entries(form).forEach(([key, value]) => {
      formData.append(`external_profile[${key}]`, value || "");
    });
    if (cvFile) {
      formData.append("external_profile[cv]", cvFile);
    }

    sh.post(`/internal/profile_pipelines/${resolvedPipelineId}/external_profiles`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then((res) => {
        onCreated?.(res.data);
        onClose();
      })
      .catch((err) => {
        if (err?.response?.status === 409) {
          setSaving(false);
          setDuplicateProfile(err.response.data.existing_profile);
          setDuplicateDialogOpen(true);
          return;
        }
        const message = err?.response?.data?.errors?.join(", ") || "Could not add profile.";
        setError(message);
      })
      .finally(() => setSaving(false));
  }, [saving, isEditMode, editingProfile, resolvedPipelineId, form, cvFile, authenticity_token, onCreated, onUpdated, onClose]);

  const handleConfirmDuplicate = useCallback(() => {
    if (saving || !resolvedPipelineId) return;

    setSaving(true);
    setError("");

    const formData = new FormData();
    formData.append("authenticity_token", authenticity_token || "");
    Object.entries(form).forEach(([key, value]) => {
      formData.append(`external_profile[${key}]`, value || "");
    });
    if (cvFile) {
      formData.append("external_profile[cv]", cvFile);
    }
    formData.append("confirm_duplicate", "true");

    sh.post(`/internal/profile_pipelines/${resolvedPipelineId}/external_profiles`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then((res) => {
        setDuplicateDialogOpen(false);
        setDuplicateProfile(null);
        onCreated?.(res.data);
        onClose();
      })
      .catch((err) => {
        const message = err?.response?.data?.errors?.join(", ") || "Could not add profile.";
        setError(message);
        setDuplicateDialogOpen(false);
        setDuplicateProfile(null);
      })
      .finally(() => setSaving(false));
  }, [saving, resolvedPipelineId, form, cvFile, authenticity_token, onCreated, onClose]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer?.types?.includes("Files")) setDragging(true);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDragging(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    dragCounter.current = 0;
    const file = e.dataTransfer?.files?.[0];
    if (file && /\.(pdf|doc|docx)$/i.test(file.name)) {
      setCvFile(file);
    }
  }, []);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 400 },
            borderLeft: "1px solid hsl(140 8% 88%)",
            backgroundColor: "#fff",
          },
        }}
      >
        <Box
          sx={{ display: "flex", flexDirection: "column", height: "100%", position: "relative" }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {dragging && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                backgroundColor: "rgba(255,255,255,0.92)",
                border: "2px dashed hsl(150 38% 17%)",
                borderRadius: "12px",
                pointerEvents: "none",
              }}
            >
              <Upload size={32} color="hsl(150, 38%, 17%)" />
              <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 38% 17%)" }}>
                Drop CV here
              </Typography>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
                PDF, DOC or DOCX
              </Typography>
            </Box>
          )}
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              py: 2,
              borderBottom: "1px solid hsl(140 8% 88%)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              {step === 2 && needsPipelinePicker && (
                <IconButton
                  size="small"
                  onClick={() => setStep(1)}
                  sx={{ p: 0.5, borderRadius: "6px", color: "hsl(150 10% 46%)", "&:hover": { backgroundColor: "hsl(140 8% 90%)" } }}
                >
                  <ArrowLeft size={16} />
                </IconButton>
              )}
              <Typography
                sx={{
                  fontSize: 14,
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  color: "hsl(150 30% 12%)",
                }}
              >
                {step === 1 ? "Select pipeline" : isEditMode ? "Edit profile" : "Add profile"}
              </Typography>
            </Stack>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ p: 0.5, borderRadius: "6px", color: "hsl(150 10% 46%)", "&:hover": { backgroundColor: "hsl(140 8% 90%)" } }}
            >
              <X size={16} />
            </IconButton>
          </Box>

          {/* Body */}
          <Box sx={{ flex: 1, overflowY: "auto", p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Step 1 — Pipeline picker */}
            {step === 1 && (
              <>
                <TextField
                  size="small"
                  fullWidth
                  value={pipelineSearch}
                  onChange={(e) => setPipelineSearch(e.target.value)}
                  placeholder="Search pipelines…"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "6px",
                      fontSize: 14,
                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      backgroundColor: "hsl(140 10% 95%)",
                      "& fieldset": { borderColor: "hsl(140 8% 88%)" },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "hsl(150 10% 46%)" }} />
                      </InputAdornment>
                    ),
                  }}
                />
                {fetchingPipelines ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} sx={{ color: "hsl(150 10% 46%)" }} />
                  </Box>
                ) : filteredPipelines.length === 0 ? (
                  <Typography sx={{ fontSize: 14, color: "hsl(150 10% 46%)", textAlign: "center", py: 3 }}>
                  No pipelines found
                  </Typography>
                ) : (
                  <List sx={{ mx: -1 }}>
                    {filteredPipelines.map((p) => (
                      <ListItemButton
                        key={p.id}
                        onClick={() => {
                          setSelectedPipeline(p);
                          setStep(2);
                        }}
                        sx={{
                          borderRadius: "6px",
                          mx: 1,
                          mb: 0.5,
                          "&:hover": { backgroundColor: "hsl(140 10% 95%)" },
                        }}
                      >
                        <ListItemText
                          primary={p.title}
                          primaryTypographyProps={{
                            sx: { fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" },
                          }}
                          secondary={p.pipeline_type === "shared" ? "Shared" : p.pipeline_type === "job_offer" ? "Job offer" : "Personal"}
                          secondaryTypographyProps={{
                            sx: { fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui" },
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </>
            )}

            {/* Step 2 — Profile form */}
            {step === 2 && (
              <>
                {needsPipelinePicker && selectedPipeline && (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: "6px",
                      backgroundColor: alpha("#079344", 0.06),
                      border: "1px solid",
                      borderColor: alpha("#079344", 0.2),
                    }}
                  >
                    <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                    Pipeline
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "#079344" }}>
                      {selectedPipeline.title}
                    </Typography>
                  </Box>
                )}

                {error && <Alert severity="error">{error}</Alert>}

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                  {[
                    { key: "first_name", label: "First name" },
                    { key: "last_name", label: "Last name" },
                  ].map((field) => (
                    <Box key={field.key}>
                      <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150 10% 46%)", mb: 0.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                        {field.label}
                      </Typography>
                      <Box
                        component="input"
                        value={form[field.key]}
                        onChange={(e) => setForm((p) => ({ ...p, [field.key]: e.target.value }))}
                        sx={{
                          width: "100%",
                          height: 40,
                          borderRadius: "6px",
                          border: "1px solid hsl(140 8% 88%)",
                          backgroundColor: "hsl(140 10% 95%)",
                          px: 1.5,
                          py: 1,
                          fontSize: 14,
                          fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                          color: "hsl(150 30% 12%)",
                          outline: "none",
                          "&::placeholder": { color: "hsl(150 10% 46%)" },
                          "&:focus": { outline: "2px solid hsl(150 38% 17%)", outlineOffset: "2px" },
                        }}
                      />
                    </Box>
                  ))}
                </Box>

                {[
                  { key: "phone", label: "Phone" },
                  { key: "email", label: "Email" },
                ].map((field) => (
                  <Box key={field.key}>
                    <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150 10% 46%)", mb: 0.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                      {field.label}
                    </Typography>
                    <Box
                      component="input"
                      value={form[field.key]}
                      onChange={(e) => setForm((p) => ({ ...p, [field.key]: e.target.value }))}
                      sx={{
                        width: "100%",
                        height: 40,
                        borderRadius: "6px",
                        border: "1px solid hsl(140 8% 88%)",
                        backgroundColor: "hsl(140 10% 95%)",
                        px: 1.5,
                        py: 1,
                        fontSize: 14,
                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                        color: "hsl(150 30% 12%)",
                        outline: "none",
                        "&::placeholder": { color: "hsl(150 10% 46%)" },
                        "&:focus": { outline: "2px solid hsl(150 38% 17%)", outlineOffset: "2px" },
                      }}
                    />
                  </Box>
                ))}

                {/* Region select */}
                <Box>
                  <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150 10% 46%)", mb: 0.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  Region
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={form.region}
                    onChange={(e) => setForm((p) => ({ ...p, region: e.target.value }))}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: 40,
                        borderRadius: "6px",
                        fontSize: 14,
                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                        color: "hsl(150 30% 12%)",
                        backgroundColor: "hsl(140 10% 95%)",
                        "& fieldset": { borderColor: "hsl(140 8% 88%)" },
                        "&:hover fieldset": { borderColor: "hsl(140 8% 88%)" },
                        "&.Mui-focused fieldset": { borderColor: "hsl(150 38% 17%)", borderWidth: 2 },
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: 14, color: "hsl(150 10% 46%)" }}>Select Region</MenuItem>
                    {englishRegionGroups.flatMap((group) => [
                      <ListSubheader
                        key={`group-${group.label}`}
                        disableSticky
                        sx={{ lineHeight: "30px", fontSize: 11, fontWeight: 700, color: "hsl(150 10% 46%)", backgroundColor: "transparent" }}
                      >
                        {group.label}
                      </ListSubheader>,
                      ...group.options.map((region) => (
                        <MenuItem key={`${group.label}-${region}`} value={region} sx={{ fontSize: 14 }}>
                          {formatRegionMessage(region)}
                        </MenuItem>
                      )),
                    ])}
                  </TextField>
                </Box>

                {/* Desired job title */}
                <Box>
                  <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150 10% 46%)", mb: 0.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  Desired job title{" "}
                    <Box component="span" sx={{ opacity: 0.7, textTransform: "lowercase" }}>(optional)</Box>
                  </Typography>
                  <Box
                    component="input"
                    value={form.desired_title}
                    onChange={(e) => setForm((p) => ({ ...p, desired_title: e.target.value }))}
                    sx={{
                      width: "100%",
                      height: 40,
                      borderRadius: "6px",
                      border: "1px solid hsl(140 8% 88%)",
                      backgroundColor: "hsl(140 10% 95%)",
                      px: 1.5,
                      py: 1,
                      fontSize: 14,
                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      color: "hsl(150 30% 12%)",
                      outline: "none",
                      "&::placeholder": { color: "hsl(150 10% 46%)" },
                      "&:focus": { outline: "2px solid hsl(150 38% 17%)", outlineOffset: "2px" },
                    }}
                  />
                </Box>

                {/* Source */}
                <Box>
                  <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150 10% 46%)", mb: 0.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  Source{" "}
                    <Box component="span" sx={{ opacity: 0.7, textTransform: "lowercase" }}>(optional)</Box>
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={form.source}
                    onChange={(e) => setForm((p) => ({ ...p, source: e.target.value }))}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: 40,
                        borderRadius: "6px",
                        fontSize: 14,
                        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                        color: "hsl(150 30% 12%)",
                        backgroundColor: "hsl(140 10% 95%)",
                        "& fieldset": { borderColor: "hsl(140 8% 88%)" },
                        "&:hover fieldset": { borderColor: "hsl(140 8% 88%)" },
                        "&.Mui-focused fieldset": { borderColor: "hsl(150 38% 17%)", borderWidth: 2 },
                      },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: 14, color: "hsl(150 10% 46%)" }}>Select source</MenuItem>
                    {SOURCES.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 14 }}>{opt.label}</MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* CV upload */}
                <Box>
                  <Typography sx={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "hsl(150 10% 46%)", mb: 0.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  CV
                  </Typography>
                  <input
                    ref={cvInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    style={{ display: "none" }}
                    onChange={(event) => setCvFile(event.target.files?.[0] || null)}
                  />
                  <Box
                    component="button"
                    type="button"
                    onClick={() => cvInputRef.current?.click()}
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      height: 40,
                      borderRadius: "6px",
                      border: "1px solid hsl(140 8% 88%)",
                      backgroundColor: "hsl(140 10% 95%)",
                      fontSize: 14,
                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      color: "hsl(150 10% 46%)",
                      cursor: "pointer",
                      transition: "background-color 0.15s ease",
                      "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.3)" },
                    }}
                  >
                    <Upload size={16} style={{ flexShrink: 0 }} />
                    {cvFile ? cvFile.name : isEditMode && editingProfile?.cv_name ? `Replace CV (current: ${editingProfile.cv_name})` : "Upload CV (PDF, DOC)"}
                  </Box>
                </Box>
              </>
            )}
          </Box>

          {/* Footer */}
          {step === 2 && (
            <Box sx={{ p: 2.5, borderTop: "1px solid hsl(140 8% 88%)" }}>
              <Button
                fullWidth
                variant="contained"
                disableElevation
                disableRipple
                onClick={handleSubmit}
                disabled={saving || !form.first_name.trim() || !form.last_name.trim()}
                sx={{
                  height: 40,
                  borderRadius: "6px",
                  textTransform: "none",
                  fontSize: 14,
                  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  backgroundColor: "hsl(150 38% 17%)",
                  color: "hsl(80 97% 72%)",
                  "&:hover": { backgroundColor: "hsl(150 38% 17%)", opacity: 0.9 },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                    backgroundColor: "hsl(150 38% 17%)",
                    color: "hsl(80 97% 72%)",
                    pointerEvents: "none",
                  },
                }}
              >
                {saving ? (isEditMode ? "Saving..." : "Adding...") : isEditMode ? "Save changes" : "Add profile"}
              </Button>
            </Box>
          )}
        </Box>
      </Drawer>

      <DuplicateProfileDialog
        open={duplicateDialogOpen}
        onClose={() => {
          setDuplicateDialogOpen(false);
          setDuplicateProfile(null);
        }}
        onImport={handleConfirmDuplicate}
        existingProfile={duplicateProfile}
      />
    </>
  );
};

export default CreateProfileDrawer;
